import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError, currencySymbol } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Utensils, QrCode, ExternalLink, Store, CreditCard, Sparkles, ScanLine, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon, color }) {
  return (
    <Card className="p-6 border border-border">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</div>
          <div className="font-heading font-extrabold text-4xl mt-3">{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </Card>
  );
}

export default function RestaurantDashboard() {
  const { user } = useAuth();
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [scans, setScans] = useState(null);
  const [scanRefreshing, setScanRefreshing] = useState(false);

  const loadScans = async () => {
    setScanRefreshing(true);
    try {
      const { data } = await api.get("/analytics/scans");
      setScans(data);
    } catch (e) {
      // silent — shown as "—"
    } finally {
      setScanRefreshing(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [r, c, i, s, sc] = await Promise.all([
          api.get("/restaurant"), api.get("/categories"), api.get("/items"), api.get("/subscription"),
          api.get("/analytics/scans").catch(() => ({ data: null })),
        ]);
        setRestaurant(r.data); setCategories(c.data); setItems(i.data); setSubscription(s.data);
        setScans(sc.data);
      } catch (e) { toast.error(formatApiError(e)); }
    };
    load();
  }, []);

  // Auto-refresh the live scan count every 30 seconds while this tab is open.
  useEffect(() => {
    const t = setInterval(loadScans, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeItems = items.filter((i) => i.available && i.in_stock).length;
  const menuUrl = restaurant ? `${window.location.origin}/m/${restaurant.slug}` : "";

  return (
    <DashboardLayout role="owner">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Dashboard</div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight" data-testid="welcome-heading">
            Hey, {user?.name || "there"} 👋
          </h1>
          <p className="text-foreground/60 mt-2">
            Here&rsquo;s a snapshot of <span className="font-semibold">{restaurant?.name || "your menu"}</span> today.
          </p>
        </div>
        <div className="flex gap-2">
          <a href={menuUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full" data-testid="preview-menu-button">
              <ExternalLink className="w-4 h-4" /> Preview menu
            </Button>
          </a>
          <Link to="/dashboard/menu"><Button className="bg-brand-orange hover:bg-[#E65C00] text-white rounded-full" data-testid="manage-menu-button">Manage menu</Button></Link>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Categories" value={categories.length} icon={BookOpen} color="bg-brand-navy" />
        <Stat label="Total items" value={items.length} icon={Utensils} color="bg-brand-orange" />
        <Stat label="Active items" value={activeItems} icon={Sparkles} color="bg-brand-navy" />
        <Stat label="Currency" value={`${currencySymbol(restaurant?.currency || "INR")} ${restaurant?.currency || "INR"}`} icon={CreditCard} color="bg-brand-orange" />
      </div>

      {/* Live scan analytics — updates every 30s */}
      <Card className="p-6 border border-border mb-10" data-testid="scan-stats-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <ScanLine className="w-4 h-4 text-brand-orange" />
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange">QR scans</div>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-veg bg-veg/10 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-veg animate-pulse" /> Live
              </span>
            </div>
            <div className="text-sm text-foreground/60 mt-1">
              How many diners opened your menu via the QR. {scans?.last_scan_at && (
                <>Last scan {new Date(scans.last_scan_at).toLocaleString()}.</>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={loadScans}
            className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition"
            aria-label="Refresh scan counts"
            data-testid="refresh-scans-btn"
          >
            <RefreshCw className={`w-4 h-4 ${scanRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">Today</div>
            <div className="font-heading font-extrabold text-3xl mt-1" data-testid="scans-today">{scans?.today ?? "—"}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">Last 7d</div>
            <div className="font-heading font-extrabold text-3xl mt-1" data-testid="scans-7d">{scans?.last_7d ?? "—"}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">Last 30d</div>
            <div className="font-heading font-extrabold text-3xl mt-1" data-testid="scans-30d">{scans?.last_30d ?? "—"}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">All time</div>
            <div className="font-heading font-extrabold text-3xl mt-1" data-testid="scans-total">{scans?.total ?? "—"}</div>
          </div>
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 border border-border lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg">Quick actions</h3>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Link to="/dashboard/menu" className="group border border-border rounded-xl p-5 hover:border-brand-orange transition" data-testid="quick-menu">
              <Utensils className="w-6 h-6 brand-orange mb-3" />
              <div className="font-heading font-semibold">Edit menu</div>
              <div className="text-xs text-foreground/60 mt-1">Add, edit or toggle items</div>
            </Link>
            <Link to="/dashboard/profile" className="group border border-border rounded-xl p-5 hover:border-brand-orange transition" data-testid="quick-profile">
              <Store className="w-6 h-6 brand-orange mb-3" />
              <div className="font-heading font-semibold">Restaurant profile</div>
              <div className="text-xs text-foreground/60 mt-1">Logo, cover, currency</div>
            </Link>
            <Link to="/dashboard/qr" className="group border border-border rounded-xl p-5 hover:border-brand-orange transition" data-testid="quick-qr">
              <QrCode className="w-6 h-6 brand-orange mb-3" />
              <div className="font-heading font-semibold">QR code</div>
              <div className="text-xs text-foreground/60 mt-1">Download & print</div>
            </Link>
          </div>
        </Card>

        <Card className="p-6 border border-border bg-brand-navy text-white">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-2">Plan</div>
          {subscription?.subscription ? (
            <>
              <div className="font-heading font-bold text-2xl">{subscription.subscription.is_trial ? "Free Trial" : subscription.subscription.plan}</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-heading font-extrabold text-3xl">₹{subscription.subscription.amount || 0}</span>
                <span className="text-white/50 text-sm">
                  {subscription.subscription.is_event_pack ? "/event" : subscription.subscription.is_trial ? "" : "/year"}
                </span>
              </div>
              <Badge className="mt-3 bg-brand-orange hover:bg-brand-orange">{subscription.subscription.is_trial ? "trial" : subscription.subscription.status}</Badge>
              <p className="text-white/60 text-xs mt-4">
                {subscription.subscription.is_event_pack
                  ? "One-off event pack."
                  : subscription.subscription.is_trial
                    ? `Trial · ${subscription.outlet_count}/${subscription.subscription.outlet_limit} outlet used.`
                    : subscription.subscription.unlimited
                      ? `${subscription.outlet_count} outlets · unlimited included.`
                      : `${subscription.outlet_count}/${subscription.subscription.outlet_limit} outlets used.`}
                {subscription.subscription.ends_at ? ` Expires ${new Date(subscription.subscription.ends_at).toLocaleDateString()}.` : ""}
              </p>
              <div className="mt-5">
                <Link to="/dashboard/billing"><Button className="w-full h-10 bg-brand-orange hover:bg-[#E65C00] rounded-full" data-testid="manage-billing-btn">
                  {subscription.subscription.is_trial ? "Upgrade plan" : "Manage billing"}
                </Button></Link>
              </div>
            </>
          ) : (
            <>
              <div className="font-heading font-bold text-2xl">No active plan</div>
              <p className="text-white/60 text-xs mt-4">Pick a plan to activate your outlets.</p>
              <div className="mt-5">
                <Link to="/dashboard/billing"><Button className="w-full h-10 bg-brand-orange hover:bg-[#E65C00] rounded-full" data-testid="choose-plan-btn">Choose a plan</Button></Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
